import { Phone, ShieldCheck, BadgeCheck, Clock, Star, HeartHandshake } from "lucide-react";
import heroImage from "../assets/hero-crew-surface-cleaner.webp";
import heroImage480 from "../assets/hero-crew-surface-cleaner-480.webp";
import heroImage640 from "../assets/hero-crew-surface-cleaner-640.webp";
import heroAvif480 from "../assets/hero-crew-surface-cleaner-480.avif";
import heroAvif640 from "../assets/hero-crew-surface-cleaner-640.avif";
import heroAvif825 from "../assets/hero-crew-surface-cleaner-825.avif";
import type { CSSProperties, ReactNode } from "react";
import { openQuoteModal } from "./quote-modal";
import { QuickLeadForm } from "./quick-lead-form";

import { SHOW_LICENSED_INSURED } from "../data/flags";

const trustItems: Array<{ icon: typeof Clock; label: ReactNode; key: string }> = [
  { icon: Star, key: "vetowned", label: "Veteran Owned & Operated" },
  { icon: Clock, key: "exp", label: "10+ Years Experience" },
  { icon: BadgeCheck, key: "free", label: "Free Quotes" },
  { icon: BadgeCheck, key: "sat", label: "Satisfaction Guaranteed" },
  { icon: HeartHandshake, key: "vet", label: "Veteran & First Responder Discounts" },
  ...(SHOW_LICENSED_INSURED
    ? [{ icon: ShieldCheck, key: "lic", label: "Licensed & Insured" as ReactNode }]
    : []),
];

const heroSrcSet = `${heroImage480} 480w, ${heroImage640} 640w, ${heroImage} 825w`;
const heroAvifSrcSet = `${heroAvif480} 480w, ${heroAvif640} 640w, ${heroAvif825} 825w`;
const heroSizes = "(max-width: 700px) 62vw, 100vw";

export function HomeHero() {
  return (
    <section className="relative isolate overflow-hidden bg-[var(--brand-deep-blue)]">
      {/* PLACEHOLDER background image — swap with a real Capital Pro job photo.
          Two layers share one cached image: a "dirty" (filtered) base and a
          "clean" overlay revealed by a diagonal CSS sweep. Animation is CSS-only,
          GPU-friendly (clip-path/opacity), and skipped under prefers-reduced-motion. */}
      <div aria-hidden="true" className="absolute inset-0 -z-20 overflow-hidden">
        <picture>
          <source type="image/avif" srcSet={heroAvifSrcSet} sizes={heroSizes} />
          <source type="image/webp" srcSet={heroSrcSet} sizes={heroSizes} />
          <img
            src={heroImage}
            alt="Capital Pro Pressure Washing crew using a surface cleaner on a Capital Region NY driveway"
            loading="eager"
            fetchPriority="high"
            decoding="sync"
            width={825}
            height={1100}
            className="absolute inset-0 h-full w-full object-cover object-center"
          />
        </picture>
        <div className="hero-dirty-veil" />
        <div className="hero-spray-streak" />
      </div>
      {/* Dark blue overlay for text legibility */}
      <div
        aria-hidden="true"
        className="absolute inset-0 -z-10"
        style={{
          background:
            "linear-gradient(to right, color-mix(in oklab, var(--brand-deep-blue) 97%, transparent) 0%, color-mix(in oklab, var(--brand-deep-blue) 93%, transparent) 45%, color-mix(in oklab, var(--brand-deep-blue) 75%, transparent) 75%, color-mix(in oklab, var(--brand-deep-blue) 55%, transparent) 100%)",
        }}
      />

      <div className="mx-auto max-w-[1200px] px-4 sm:px-6 py-16 sm:py-24 lg:py-32 lg:flex lg:items-start lg:gap-10">
        <div className="max-w-2xl lg:flex-1">

          <p
            className="hero-in hero-in-eyebrow relative z-10 text-[#FF8A93] font-bold uppercase tracking-[0.18em] text-xs sm:text-sm"
            style={{
              fontFamily: "var(--font-display)",
              textShadow:
                "0 1px 2px rgba(0,0,0,0.55), 0 0 12px rgba(11,61,122,0.7)",
            }}
          >
            Capital City Power Washing Pros · Albany, NY
          </p>

          <h1
            className="hero-in hero-in-h1 mt-4 text-white font-bold uppercase leading-[1.05] text-4xl sm:text-5xl lg:text-6xl"
            style={{ fontFamily: "var(--font-display)" }}
          >
            Pressure Washing in Albany &amp; the Capital Region
          </h1>

          <p
            className="hero-in hero-in-sub mt-5 text-white text-base sm:text-lg max-w-xl leading-relaxed"
            style={{ textShadow: "0 1px 3px rgba(0,0,0,0.45)" }}
          >
            Soft washing and power washing that makes your home look new again — house
            washing, roof washing, concrete and more, across Albany, Saratoga, Schenectady
            and the entire Capital Region of New York. 10 years in business. Free quotes.
          </p>

          <div className="hero-in hero-in-ctas mt-7 flex flex-col sm:flex-row gap-3 sm:gap-4">
            <button
              type="button"
              onClick={() => openQuoteModal()}
              className="btn-pop btn-shimmer btn-jiggle inline-flex items-center justify-center min-h-[52px] px-6 rounded-md bg-[var(--brand-red)] text-white font-bold uppercase tracking-wide text-base hover:bg-[var(--brand-red-hover)] transition-colors"
            >
              Get Free Quote
            </button>
            <a
              href="tel:+15189001913"
              className="btn-pop inline-flex items-center justify-center gap-2 min-h-[52px] px-6 rounded-md bg-white text-[var(--brand-deep-blue)] font-bold text-base hover:bg-[var(--brand-light-gray)] transition-colors"
            >
              <Phone className="h-5 w-5" />
              Call (518) 900-1913
            </a>
          </div>

          <p className="hero-in hero-in-ctas mt-4">
            <span
              className="inline-flex items-center gap-2 rounded-full bg-[var(--brand-red)] px-3.5 py-1.5 text-white text-xs uppercase tracking-[0.12em]"
              style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
            >
              Save 10% when you book 2+ services
            </span>
          </p>

          <ul className="mt-8 flex flex-wrap gap-x-5 gap-y-3">
            {trustItems.map(({ icon: Icon, label, key }, i) => (
              <li
                key={key}
                className="hero-in hero-in-trust flex items-center gap-2 text-white text-sm font-medium"
                style={
                  {
                    ["--hero-trust-i" as string]: i,
                    textShadow: "0 1px 3px rgba(0,0,0,0.45)",
                  } as CSSProperties
                }
              >
                <Icon className="h-4 w-4 text-[var(--brand-red)]" aria-hidden="true" />
                <span>{label}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Desktop-only quick lead form for visitors who want the fast path */}
        <div className="hidden lg:block w-[340px] shrink-0">
          <QuickLeadForm source="Hero Quick Quote" />
        </div>
      </div>

    </section>
  );
}